import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (data) setForm(data); }, [data]);

  if (isLoading || !form) return <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", {
        desired_retention: form.desired_retention, new_cards_per_day: Number(form.new_cards_per_day),
        max_reviews_per_day: Number(form.max_reviews_per_day), allow_topic_practice: form.allow_topic_practice,
        include_new_cards: form.include_new_cards,
        show_intervals: form.show_intervals, watermark_cards: form.watermark_cards,
      });
      toast.success("Settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-400">Admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Settings</h1>
      </div>

      <Section title="Scheduling">
        <div>
          <div className="flex justify-between"><Label>Desired retention</Label><span className="font-mono text-sm text-slate-600">{Math.round(form.desired_retention * 100)}%</span></div>
          <Slider min={80} max={97} step={1} value={[Math.round(form.desired_retention * 100)]}
            onValueChange={(v) => setForm({ ...form, desired_retention: v[0] / 100 })} className="mt-3" data-testid="retention-slider" />
          <p className="text-xs text-slate-400 mt-2">How much students should remember. Higher = more frequent reviews. Not shown to students.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>New cards / day</Label><Input type="number" value={form.new_cards_per_day} onChange={(e) => setForm({ ...form, new_cards_per_day: e.target.value })} data-testid="new-cards-input" className="mt-1.5" /></div>
          <div><Label>Max reviews / day (0 = unlimited)</Label><Input type="number" value={form.max_reviews_per_day} onChange={(e) => setForm({ ...form, max_reviews_per_day: e.target.value })} className="mt-1.5" /></div>
        </div>
      </Section>

      <Section title="Study">
        <ToggleRow label="Introduce new cards in daily revision" desc="When off, daily revision only shows cards students have already seen — no new cards are introduced. Toggling this never changes existing progress or schedules." checked={form.include_new_cards} onChange={(v) => setForm({ ...form, include_new_cards: v })} testid="include-new-switch" />
        <ToggleRow label="Allow topic practice" desc="Let students revise a specific topic for extra practice." checked={form.allow_topic_practice} onChange={(v) => setForm({ ...form, allow_topic_practice: v })} testid="allow-practice-switch" />
        <ToggleRow label="Show review intervals on buttons" desc="Display the next-review time above Again/Hard/Good/Easy." checked={form.show_intervals} onChange={(v) => setForm({ ...form, show_intervals: v })} testid="show-intervals-switch" />
      </Section>

      <Section title="Security">
        <ToggleRow label="Watermark cards" desc="Overlay a subtle student identifier on card content as a deterrent." checked={form.watermark_cards} onChange={(v) => setForm({ ...form, watermark_cards: v })} testid="watermark-switch" />
      </Section>

      <Button onClick={save} disabled={busy} data-testid="save-settings-button" className="bg-slate-900 hover:bg-slate-800">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}
      </Button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4">
      <h2 className="font-display text-lg font-semibold text-slate-900">{title}</h2>
      {children}
    </div>
  );
}
function ToggleRow({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div><Label>{label}</Label><p className="text-xs text-slate-400 mt-0.5">{desc}</p></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
